import { Apollo } from 'src/gql/generated';

export type GoverningDocumentMemberSubset = Pick<
  Apollo.GoverningDocumentMember,
  '__typename' | 'direction' | 'documentId' | 'relationshipType'
>;

export interface GoverningDocumentCitationSubset extends Pick<
  Apollo.GoverningDocumentCitation,
  '__typename' | 'documentId'
> {
  document: Pick<Apollo.Document, '__typename' | 'id' | 'name'> | null;
}

export interface GoverningDocumentsSubset extends Pick<Apollo.GoverningDocuments, '__typename'> {
  citations: GoverningDocumentCitationSubset[];
  members: GoverningDocumentMemberSubset[];
}

export interface GoverningDocumentSourceRow {
  documentId: UUID;
  relationshipLabel: string | null;
  relationshipTooltip: string | null;
  title: string;
}

/** What one cited document's hover card shows. */
export interface CitationCardData {
  relationshipLabel: string | null;
  /** Directional sentence for the role ("Amendment #1 amends this document."). The card shows the label only. */
  relationshipTooltip: string | null;
  title: string;
  /** The row document's own entry, which shows "This document" and no chip. */
  isRowDocument?: boolean;
  /** The question the cell answers: a badge click lands the destination's AI Properties on it. */
  questionId?: string | null;
}

const UNTITLED_DOCUMENT_TITLE = 'Untitled document';

type GoverningDocumentRole = 'child' | 'parent';

/**
 * Which side of the relationship the source sits on: "child" means the source
 * acts on the target (an amendment, or a SOW governed by it), "parent" means
 * the source is the target's master. `MemberModifiesAncestorLine` counts as
 * "child", since the source is still the modifier, just of an ancestor.
 */
const GOVERNING_DOCUMENT_DIRECTION_ROLE = {
  [Apollo.GoverningDocumentDirection.MemberModifiesAncestorLine]: 'child',
  [Apollo.GoverningDocumentDirection.MemberModifiesTarget]: 'child',
  [Apollo.GoverningDocumentDirection.MemberPartOfTarget]: 'child',
  [Apollo.GoverningDocumentDirection.TargetModifiesMember]: 'parent',
  [Apollo.GoverningDocumentDirection.TargetPartOfMember]: 'parent',
} satisfies Record<Apollo.GoverningDocumentDirection, GoverningDocumentRole>;

/**
 * One-word noun chip for a source in the child role. Kept separate from the
 * verb-based table in `SelectRelatedDocumentsWithRelationshipDialog`, which
 * backs the relationships UI's own copy. `Mention`, `Related` and `Flowdown`
 * are unreachable (the server drops its NONE bucket first) but the field's
 * type is the full union, so the map still needs them to stay exhaustive.
 */
const CHILD_ROLE_NOUN = {
  [Apollo.ChildRelationshipType.Amendment]: 'Amendment',
  [Apollo.ChildRelationshipType.Appendix]: 'Appendix',
  [Apollo.ChildRelationshipType.Assignment]: 'Assignment',
  [Apollo.ChildRelationshipType.Correction]: 'Correction',
  [Apollo.ChildRelationshipType.Exhibit]: 'Exhibit',
  [Apollo.ChildRelationshipType.Extension]: 'Extension',
  [Apollo.ChildRelationshipType.Flowdown]: 'Flowdown',
  [Apollo.ChildRelationshipType.GovernedBy]: 'Governed',
  [Apollo.ChildRelationshipType.Incorporation]: 'Incorporation',
  [Apollo.ChildRelationshipType.Mention]: 'Mention',
  [Apollo.ChildRelationshipType.Novation]: 'Novation',
  [Apollo.ChildRelationshipType.Related]: 'Related',
  [Apollo.ChildRelationshipType.Renewal]: 'Renewal',
  [Apollo.ChildRelationshipType.Schedule]: 'Schedule',
  [Apollo.ChildRelationshipType.Supplement]: 'Supplement',
  [Apollo.ChildRelationshipType.Supersede]: 'Restatement',
  [Apollo.ChildRelationshipType.Termination]: 'Termination',
} satisfies Record<Apollo.ChildRelationshipType, string>;

/**
 * Third-person-singular predicate for the tooltip sentence. In the child role
 * it describes what the source does to the document it acts on; in the parent
 * role, what the target does to the source. Exhaustive for the same reason as
 * `CHILD_ROLE_NOUN`.
 */
const CHILD_ROLE_PREDICATE = {
  [Apollo.ChildRelationshipType.Amendment]: 'amends',
  [Apollo.ChildRelationshipType.Appendix]: 'is an appendix to',
  [Apollo.ChildRelationshipType.Assignment]: 'assigns',
  [Apollo.ChildRelationshipType.Correction]: 'corrects',
  [Apollo.ChildRelationshipType.Exhibit]: 'is an exhibit to',
  [Apollo.ChildRelationshipType.Extension]: 'extends',
  [Apollo.ChildRelationshipType.Flowdown]: 'flows down to',
  [Apollo.ChildRelationshipType.GovernedBy]: 'is governed by',
  [Apollo.ChildRelationshipType.Incorporation]: 'incorporates',
  [Apollo.ChildRelationshipType.Mention]: 'mentions',
  [Apollo.ChildRelationshipType.Novation]: 'novates',
  [Apollo.ChildRelationshipType.Related]: 'is related to',
  [Apollo.ChildRelationshipType.Renewal]: 'renews',
  [Apollo.ChildRelationshipType.Schedule]: 'is a schedule to',
  [Apollo.ChildRelationshipType.Supplement]: 'supplements',
  [Apollo.ChildRelationshipType.Supersede]: 'supersedes',
  [Apollo.ChildRelationshipType.Termination]: 'terminates',
} satisfies Record<Apollo.ChildRelationshipType, string>;

function isParentRole(direction: Apollo.GoverningDocumentDirection): boolean {
  return GOVERNING_DOCUMENT_DIRECTION_ROLE[direction] === 'parent';
}

/**
 * The noun chip label: the modifier type when the source acts on the target,
 * "Master" when the source is the agreement above. `Supersede` is the one
 * exception, reading "Original", since a superseding source replaced that
 * agreement rather than remaining its master.
 */
export function getGoverningDocumentRelationshipLabel(
  member: Pick<GoverningDocumentMemberSubset, 'direction' | 'relationshipType'>
): string {
  if (isParentRole(member.direction)) {
    return member.relationshipType === Apollo.ChildRelationshipType.Supersede
      ? 'Original'
      : 'Master';
  }
  return CHILD_ROLE_NOUN[member.relationshipType];
}

/**
 * The tooltip sentence for a source's relationship chip: "{source} amends
 * this document." in the child role, "This document amends {source}." in the
 * parent role, which also reads correctly for a passive predicate like "is
 * governed by". The ancestor-line wording says "an earlier agreement that
 * governs this document" because `GoverningDocumentMember` carries only the
 * source's own id, never the ancestor's, so the client cannot name it.
 */
export function getGoverningDocumentRelationshipTooltip(
  member: Pick<GoverningDocumentMemberSubset, 'direction' | 'relationshipType'>,
  sourceTitle: string
): string {
  const predicate = CHILD_ROLE_PREDICATE[member.relationshipType];

  if (member.direction === Apollo.GoverningDocumentDirection.MemberModifiesAncestorLine) {
    return `${sourceTitle} ${predicate} an earlier agreement that governs this document.`;
  }

  return isParentRole(member.direction)
    ? `This document ${predicate} ${sourceTitle}.`
    : `${sourceTitle} ${predicate} this document.`;
}

/**
 * One row per distinct cited document, carrying the chip label and tooltip
 * from the first member matching it. A citation with no matching member (one
 * the truncation cap dropped) still gets a row, just without a chip.
 */
export function deriveGoverningDocumentSources(
  governingDocuments: GoverningDocumentsSubset | null | undefined
): GoverningDocumentSourceRow[] {
  if (!governingDocuments?.citations.length) return [];

  const { citations, members } = governingDocuments;
  const seenDocumentIds = new Set<UUID>();
  const rows: GoverningDocumentSourceRow[] = [];

  for (const citation of citations) {
    if (seenDocumentIds.has(citation.documentId)) continue;
    seenDocumentIds.add(citation.documentId);

    const member = members.find(candidate => candidate.documentId === citation.documentId);
    const title = citation.document?.name || UNTITLED_DOCUMENT_TITLE;

    rows.push({
      documentId: citation.documentId,
      relationshipLabel: member ? getGoverningDocumentRelationshipLabel(member) : null,
      relationshipTooltip: member ? getGoverningDocumentRelationshipTooltip(member, title) : null,
      title,
    });
  }

  return rows;
}

export interface RowDocumentCitationSource {
  documentId: UUID;
  /** Blank titles render as "Untitled Document", with the "This document" caption still underneath. */
  title?: string | null;
}

/**
 * The hover card behind each citation badge, keyed by document id: the row
 * document's own entry plus one per source. Badges number themselves with the
 * CSS counter in order of appearance, so a citation of a document in neither
 * gets no card and keeps its number.
 */
export function getCitationCardDataByDocumentId(args: {
  rowDocument: RowDocumentCitationSource;
  sources: GoverningDocumentSourceRow[];
  /** The question the cell answers; the same for every citation in its reasoning. */
  questionId?: string | null;
}): ReadonlyMap<string, CitationCardData> {
  const { questionId, rowDocument, sources } = args;
  const citationCardDataByDocumentId = new Map<string, CitationCardData>(
    sources.map(source => [
      source.documentId,
      {
        questionId,
        relationshipLabel: source.relationshipLabel,
        relationshipTooltip: source.relationshipTooltip,
        title: source.title,
      },
    ])
  );
  citationCardDataByDocumentId.set(rowDocument.documentId, {
    isRowDocument: true,
    questionId,
    relationshipLabel: null,
    relationshipTooltip: null,
    title: rowDocument.title ?? '',
  });

  return citationCardDataByDocumentId;
}
